import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardBody, CardHeader, Col, Row } from 'reactstrap';
import {
  Bar,
  BarChart,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatNumber, formatPct, windowDurationMinutes } from './formatters';

// Mapeo categoria → color. Los colores estan pensados para diferenciar
// rapidamente categorias "buenas" (verde), "tibias" (ambar/naranja),
// "malas" (rojo) y operativas (gris/azul).
const CATEGORY_COLORS = {
  'ENTREGA VALIDADA': '#10B981',
  'ENTREGA CON EVIDENCIA INSUFICIENTE': '#F59E0B',
  'ENTREGA NO EXITOSA VALIDADA': '#F97316',
  'ENTREGA RECHAZADA': '#DC2626',
  'NO PROCESABLE': '#6B7280',
  'Parcialmente procesado': '#3B82F6',
  '(null)': '#9CA3AF',
};

const KNOWN_CATEGORIES = new Set(Object.keys(CATEGORY_COLORS));
const OTHERS_COLOR = '#94A3B8';
const TOP_N = 6;

// Buckets de origen acordados con calidad-ia v2. Orden y color fijos para
// que la barra apilada no parpadee entre snapshots.
const SOURCE_BUCKETS = [
  { id: 'live', label: 'Live', color: '#3B82F6' },
  { id: 'batch', label: 'Batch', color: '#A855F7' },
  { id: 'backfill', label: 'Backfill', color: '#F59E0B' },
  { id: 'manual', label: 'Manual', color: '#0EA5E9' },
  { id: 'reconciler', label: 'Reconciler', color: '#94A3B8' },
];

/**
 * Pequeño KPI inline para el strip superior del throughput.
 */
const InlineKpi = ({ label, value, color, sub }) => (
  <div className="ai-perf-inline-kpi">
    <div style={{ fontSize: 11, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
      {label}
    </div>
    <div style={{ fontSize: 22, fontWeight: 700, color: color || 'var(--text-primary)' }}>
      {value}
    </div>
    {sub && (
      <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{sub}</div>
    )}
  </div>
);

const ThroughputSection = ({ data }) => {
  const { t } = useTranslation();
  const tp = data?.throughput || {};
  const winMin = windowDurationMinutes(data?.window);
  const total = Number(tp.total || 0);
  const done = Number(tp.done || 0);
  const failed = Number(tp.failed || 0);
  const successRate = total > 0 ? done / total : 0;
  const ratePerMin = winMin && winMin > 0 ? total / winMin : null;

  // Si calidad-ia ya implemento la coordinacion (ver
  // delivery-quality/docs/calidad-ia-metrics-coordination.md), `unique_orders`
  // viene en el payload y se promueve a KPI principal porque cuenta paquetes
  // unicos en lugar de jobs. Si no, se cae a `total` (jobs).
  const unique = data?.unique_orders || tp.unique_orders;
  const uniqueTotal = unique?.total != null ? Number(unique.total) : null;
  const dedupRatio =
    uniqueTotal != null && total > 0 ? uniqueTotal / total : null;

  // by_source: 5 buckets fijos. Calculamos la barra apilada y el % de cada
  // bucket sobre el total para que el usuario entienda que de N jobs solo
  // M corresponden a trafico real (live).
  const bySource = useMemo(() => {
    const raw = tp.by_source;
    if (!raw || typeof raw !== 'object') return null;
    const entries = SOURCE_BUCKETS.map((b) => ({
      ...b,
      value: Number(raw[b.id] || 0),
    }));
    const sumKnown = entries.reduce((acc, e) => acc + e.value, 0);
    if (sumKnown <= 0) return null;
    return {
      entries,
      total: sumKnown,
      live: entries.find((e) => e.id === 'live')?.value || 0,
    };
  }, [tp.by_source]);

  // Donut: solo dos slices (done vs failed). Quitamos % en el centro.
  const donutData = useMemo(
    () => [
      { name: 'Done', value: done, key: 'done' },
      { name: 'Failed', value: failed, key: 'failed' },
    ],
    [done, failed],
  );

  // Bar chart de categorias: top N + "Otros" (suma claves desconocidas y
  // muestra las claves originales en tooltip).
  const { categoryBars, othersDetail } = useMemo(() => {
    const byKey = tp.by_resultado_final || {};
    const entries = Object.entries(byKey).map(([k, v]) => ({ key: k, value: Number(v || 0) }));

    // Separa conocidas y "otras" (claves truncadas/raras del parser viejo).
    const known = entries.filter((e) => KNOWN_CATEGORIES.has(e.key));
    const unknown = entries.filter((e) => !KNOWN_CATEGORIES.has(e.key));

    const sortedKnown = [...known].sort((a, b) => b.value - a.value);
    const visible = sortedKnown.slice(0, TOP_N);
    const overflow = sortedKnown.slice(TOP_N);
    const othersTotal =
      overflow.reduce((acc, e) => acc + e.value, 0) +
      unknown.reduce((acc, e) => acc + e.value, 0);

    const detail = [...overflow, ...unknown]
      .sort((a, b) => b.value - a.value)
      .slice(0, 12);

    const result = visible.map((e) => ({
      name: e.key,
      value: e.value,
      color: CATEGORY_COLORS[e.key] || OTHERS_COLOR,
    }));

    if (othersTotal > 0) {
      result.push({
        name: 'Otros',
        value: othersTotal,
        color: OTHERS_COLOR,
        _detail: detail,
      });
    }

    return { categoryBars: result, othersDetail: detail };
  }, [tp.by_resultado_final]);

  const renderCategoryTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload[0]) return null;
    const p = payload[0].payload;
    const isOthers = p.name === 'Otros' && p._detail?.length;
    return (
      <div
        className="recharts-default-tooltip"
        style={{
          background: 'var(--surface-primary)',
          border: '1px solid var(--border-primary)',
          padding: 8,
          borderRadius: 6,
          maxWidth: 320,
        }}
      >
        <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{p.name}</div>
        <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
          {formatNumber(p.value)} jobs ({formatPct(total > 0 ? p.value / total : 0)})
        </div>
        {isOthers && (
          <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-tertiary)' }}>
            <div style={{ marginBottom: 2 }}>Incluye:</div>
            {p._detail.map((d) => (
              <div key={d.key}>
                · <code>{d.key.length > 28 ? `${d.key.slice(0, 28)}…` : d.key}</code>:{' '}
                {formatNumber(d.value)}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <h5
        className="mb-3 ai-perf-section-title"
        style={{ fontSize: 14, color: 'var(--text-secondary)', fontWeight: 600 }}
      >
        <i className="fas fa-chart-pie me-2" style={{ color: '#3B82F6' }} />
        {t('Throughput en la ventana')}
        {data?.window?.source && (
          <small className="ms-2" style={{ fontWeight: 400, color: 'var(--text-tertiary)' }}>
            ({data.window.source === 'window' ? t('ventana relativa') : t('rango absoluto')})
          </small>
        )}
      </h5>

      {/* Strip de KPIs */}
      <Row className="g-3 mb-3">
        <Col xs="6" md="3">
          <Card className="metric-card h-100">
            <CardBody>
              {uniqueTotal != null ? (
                <InlineKpi
                  label={t('Paquetes únicos')}
                  value={formatNumber(uniqueTotal)}
                  sub={
                    dedupRatio != null
                      ? `${formatNumber(total)} jobs · ratio ${(1 / dedupRatio).toFixed(2)}x`
                      : `${formatNumber(total)} jobs`
                  }
                />
              ) : (
                <InlineKpi
                  label={t('Total cerrado (jobs)')}
                  value={formatNumber(total)}
                  sub={t('incluye reintentos')}
                />
              )}
            </CardBody>
          </Card>
        </Col>
        <Col xs="6" md="3">
          <Card className="metric-card h-100">
            <CardBody>
              <InlineKpi
                label={t('Done')}
                value={formatNumber(done)}
                color="#10B981"
                sub={formatPct(successRate)}
              />
            </CardBody>
          </Card>
        </Col>
        <Col xs="6" md="3">
          <Card className="metric-card h-100">
            <CardBody>
              <InlineKpi
                label={t('Failed')}
                value={formatNumber(failed)}
                color="#DC2626"
                sub={total > 0 ? formatPct(failed / total) : '—'}
              />
            </CardBody>
          </Card>
        </Col>
        <Col xs="6" md="3">
          <Card className="metric-card h-100">
            <CardBody>
              <InlineKpi
                label={t('Tasa por min')}
                value={ratePerMin != null ? ratePerMin.toFixed(1) : '—'}
                sub={winMin ? `${t('ventana')}: ${winMin >= 60 ? (winMin / 60).toFixed(1) + ' h' : winMin.toFixed(0) + ' min'}` : ''}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Origen del trabajo (by_source). Stacked bar + leyenda. Solo cuando
          calidad-ia v2 entrega `throughput.by_source`. */}
      {bySource && (
        <Card className="mb-3 ai-perf-by-source">
          <CardBody style={{ paddingTop: 10, paddingBottom: 12 }}>
            <div
              style={{
                fontSize: 11,
                color: 'var(--text-tertiary)',
                textTransform: 'uppercase',
                letterSpacing: 0.4,
                marginBottom: 6,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                flexWrap: 'wrap',
                gap: 8,
              }}
            >
              <span>
                <i className="fas fa-layer-group me-1" style={{ color: '#3B82F6' }} />
                {t('Origen del trabajo')}
              </span>
              <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                Live: <b>{formatNumber(bySource.live)}</b> ·{' '}
                {formatPct(bySource.live / bySource.total)} {t('del total')} ·{' '}
                {formatNumber(bySource.total - bySource.live)} no-live
              </span>
            </div>
            <div
              className="ai-perf-stacked-bar"
              style={{
                display: 'flex',
                width: '100%',
                height: 18,
                borderRadius: 4,
                overflow: 'hidden',
                background: 'var(--surface-secondary, #F1F5F9)',
              }}
              title={bySource.entries
                .filter((e) => e.value > 0)
                .map(
                  (e) =>
                    `${e.label}: ${formatNumber(e.value)} (${formatPct(
                      e.value / bySource.total,
                    )})`,
                )
                .join('  |  ')}
            >
              {bySource.entries.map((e) =>
                e.value > 0 ? (
                  <div
                    key={e.id}
                    style={{
                      flexGrow: e.value,
                      background: e.color,
                      minWidth: 2,
                      transition: 'flex-grow 200ms ease',
                    }}
                  />
                ) : null,
              )}
            </div>
            <div
              className="d-flex flex-wrap gap-3 mt-2"
              style={{ fontSize: 11, color: 'var(--text-secondary)' }}
            >
              {bySource.entries.map((e) => (
                <div key={e.id} className="d-flex align-items-center gap-1">
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 2,
                      background: e.color,
                      opacity: e.value > 0 ? 1 : 0.3,
                    }}
                  />
                  <span style={{ opacity: e.value > 0 ? 1 : 0.6 }}>
                    {e.label} · <b>{formatNumber(e.value)}</b>{' '}
                    {e.value > 0 && (
                      <span style={{ color: 'var(--text-tertiary)' }}>
                        ({formatPct(e.value / bySource.total)})
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      <Row className="g-3 mb-4">
        {/* Donut done/failed */}
        <Col xs="12" lg="5">
          <Card className="h-100">
            <CardHeader>{t('Éxito vs Falla')}</CardHeader>
            <CardBody style={{ height: 320, position: 'relative' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={donutData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={64}
                    outerRadius={100}
                    paddingAngle={2}
                  >
                    <Cell fill="#10B981" />
                    <Cell fill="#DC2626" />
                  </Pie>
                  <Tooltip
                    formatter={(value, name) => [
                      `${formatNumber(value)} (${formatPct(total > 0 ? value / total : 0)})`,
                      name,
                    ]}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
              {/* Etiqueta central */}
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 30,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                }}
              >
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t('Éxito')}</div>
                <div style={{ fontSize: 26, fontWeight: 700, color: '#10B981' }}>
                  {formatPct(successRate)}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                  {formatNumber(total)} {t('totales')}
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>

        {/* Bar chart horizontal de categorias */}
        <Col xs="12" lg="7">
          <Card className="h-100">
            <CardHeader>{t('Resultado final (top {{n}})', { n: TOP_N })}</CardHeader>
            <CardBody style={{ height: 320 }}>
              {categoryBars.length === 0 ? (
                <div className="text-center py-5" style={{ color: 'var(--text-tertiary)' }}>
                  {t('Sin datos en la ventana seleccionada.')}
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={categoryBars}
                    layout="vertical"
                    margin={{ top: 8, right: 16, bottom: 8, left: 16 }}
                  >
                    <XAxis type="number" />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={210}
                      tick={{ fontSize: 11 }}
                      tickFormatter={(v) => (v.length > 28 ? `${v.slice(0, 28)}…` : v)}
                    />
                    <Tooltip content={renderCategoryTooltip} />
                    <Bar dataKey="value" name={t('Jobs')} radius={[0, 4, 4, 0]}>
                      {categoryBars.map((b) => (
                        <Cell key={b.name} fill={b.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
              {othersDetail.length > 0 && (
                <div
                  style={{
                    fontSize: 11,
                    color: 'var(--text-tertiary)',
                    marginTop: 6,
                  }}
                >
                  <i className="fas fa-info-circle me-1" />
                  {t('"Otros" agrupa categorías minoritarias y claves de parser antiguas. Pasa el cursor para ver el detalle.')}
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default ThroughputSection;
